import { Controller } from 'react-hook-form'
import { cn } from '@lib/utils/helpers'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { IntegratedFileSelect } from '@/components/ui/IntegratedFileSelect'
import companyFormStyles from '../../styles/organizationCard.module.css'
import { cleanPhoneNumber, formatPhoneNumber } from '@/features/invitee/utils/inviteeFormatters'
import type { OrganizationEditSectionProps } from '../../types/components'
import { Building2, CreditCard, Image as ImageIcon, Palette, Mail } from 'lucide-react'
import { EmailFooterPreviewContent } from './EmailFooterPreviewContent'
import { OrganizationLogoPreview } from './OrganizationLogoPreview'
import { buildOrganizationLogoDownloadAction } from '../../utils/editPage'


export function OrganizationProfileTab({ ctx }: OrganizationEditSectionProps) {
  return (
        <div
          id="org-panel-orgprofile"
          role="tabpanel"
          aria-labelledby="org-tab-orgprofile"
          hidden={ctx.selectedTab !== 'orgprofile'}
        >
        <div className="space-y-6">
        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <Building2 className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Organization information
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-12 sm:items-start">
              <div className={cn("sm:col-span-4", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-name">
                  Organization name
                </label>
                <input
                  id="org-name"
                  type="text"
                  className={cn(companyFormStyles.customFormControl, ctx.errors.org_name && 'companyFormStyles.formControlError')}
                  autoComplete="organization"
                  {...ctx.register('org_name')}
                />
                {ctx.errors.org_name && <p className={companyFormStyles.errorText}>{ctx.errors.org_name.message}</p>}
              </div>
              <div className={cn("sm:col-span-2", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-abbrev">
                  Organization abbreviation
                </label>
                <input
                  id="org-abbrev"
                  type="text"
                  maxLength={3}
                  className={cn(companyFormStyles.customFormControl, 'uppercase', ctx.errors.org_abbrev && 'companyFormStyles.formControlError')}
                  aria-label="Abbreviation"
                  {...ctx.register('org_abbrev')}
                />
                {ctx.errors.org_abbrev && (
                  <p className={companyFormStyles.errorText}>{ctx.errors.org_abbrev.message}</p>
                )}
              </div>
              <div className={cn("sm:col-span-4", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-website">
                  Organization website
                </label>
                <input
                  id="org-website"
                  type="url"
                  placeholder="https://..."
                  className={cn(
                    companyFormStyles.customFormControl,
                    ctx.errors.org_website && 'companyFormStyles.formControlError',
                  )}
                  {...ctx.register('org_website')}
                />
                {ctx.errors.org_website && (
                  <span role="alert" className={companyFormStyles.errorText}>
                    {ctx.errors.org_website.message}
                  </span>
                )}
              </div>
              <div className={cn("sm:col-span-2", companyFormStyles.formGroup)} role="group" aria-labelledby="org-charity-label">
                <span id="org-charity-label" className={companyFormStyles.formLabel}>
                  Charity
                </span>
                <div className={companyFormStyles.radioGroup}>
                  <label className={companyFormStyles.radioLabel}>
                    <input
                      id="org-charity-yes"
                      type="radio"
                      value="1"
                      className={companyFormStyles.radioInput}
                      {...ctx.register('charity')}
                    />
                    <span className={companyFormStyles.radioLabelText}>Yes</span>
                  </label>
                  <label className={companyFormStyles.radioLabel}>
                    <input
                      id="org-charity-no"
                      type="radio"
                      value="0"
                      className={companyFormStyles.radioInput}
                      {...ctx.register('charity')}
                    />
                    <span className={companyFormStyles.radioLabelText}>No</span>
                  </label>
                </div>
                {ctx.errors.charity && (
                  <span role="alert" className={companyFormStyles.errorText}>
                    {ctx.errors.charity.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <CreditCard className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Billing information
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-12 sm:items-start">
              <div className={cn("sm:col-span-3", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-phone">
                  Phone number
                </label>
                <Controller
                  name="org_phone"
                  control={ctx.control}
                  render={({ field }) => (
                    <input
                      id="org-phone"
                      type="tel"
                      inputMode="tel"
                      placeholder="(xxx) xxx-xxxx"
                      autoComplete="tel"
                      className={cn(
                        companyFormStyles.customFormControl,
                        ctx.errors.org_phone && 'companyFormStyles.formControlError',
                      )}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const cleaned = cleanPhoneNumber(e.target.value)
                        field.onChange(formatPhoneNumber(cleaned))
                      }}
                    />
                  )}
                />
                {ctx.errors.org_phone && (
                  <p className={companyFormStyles.errorText}>{ctx.errors.org_phone.message}</p>
                )}
              </div>
              <div className={cn("sm:col-span-3", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-email">
                  Email address
                </label>
                <input
                  id="org-email"
                  type="email"
                  className={cn(companyFormStyles.customFormControl, ctx.errors.org_email && 'companyFormStyles.formControlError')}
                  autoComplete="email"
                  {...ctx.register('org_email')}
                />
                {ctx.errors.org_email && (
                  <p className={companyFormStyles.errorText}>{ctx.errors.org_email.message}</p>
                )}
              </div>
              <div className={cn("sm:col-span-6", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-billing-address">
                  Billing address
                </label>
                <input
                  id="org-billing-address"
                  type="text"
                  className={companyFormStyles.customFormControl}
                  autoComplete="street-address"
                  {...ctx.register('org_address')}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-12 sm:items-start mt-6">
              <div className={cn("sm:col-span-4", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-city">
                  City
                </label>
                <input
                  id="org-city"
                  type="text"
                  className={companyFormStyles.customFormControl}
                  autoComplete="address-level2"
                  {...ctx.register('org_city')}
                />
              </div>
              <div className={cn("sm:col-span-4", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-state">
                  State
                </label>
                <select
                  id="org-state"
                  className={cn(companyFormStyles.customFormControl, 'bg-white')}
                  {...ctx.register('org_state')}
                >
                  <option value="">Select state</option>
                  {ctx.statesSorted.map((s) => (
                    <option key={s.state_code || s.id} value={s.state_code}>
                      {s.state_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={cn("sm:col-span-4", companyFormStyles.formGroup)}>
                <label className={companyFormStyles.formLabel} htmlFor="org-zip">
                  Zipcode
                </label>
                <input
                  id="org-zip"
                  type="text"
                  inputMode="numeric"
                  aria-invalid={ctx.errors.org_zipcode ? true : undefined}
                  className={cn(
                    companyFormStyles.customFormControl,
                    ctx.errors.org_zipcode && 'companyFormStyles.formControlError',
                  )}
                  autoComplete="postal-code"
                  {...ctx.register('org_zipcode')}
                  maxLength={6}
                />
                {ctx.errors.org_zipcode && (
                  <span role="alert" className={companyFormStyles.errorText}>
                    {ctx.errors.org_zipcode.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <ImageIcon className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Logo information
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className="grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-3">
              <div className={companyFormStyles.formGroup}>
                <label className={companyFormStyles.formLabel}>Organization logo</label>
                <IntegratedFileSelect
                  displayName={ctx.displayLogoLine('org_logo')}
                  accept={ctx.logoImageAccept}
                  emptyLabel="No file selected"
                  browseLabel="Select"
                  disabled={ctx.submitting}
                  onChange={(e) => ctx.handleLogoFileChange('org_logo', e)}
                  data-testid="org-logo-upload"
                  className={cn(ctx.primaryLogoRequiredError && 'companyFormStyles.formControlError')}
                />
                <p className={companyFormStyles.infoText}>
                  Only JPG, JPEG &amp; PNG files are allowed. Preferred format is PNG for transparency.
                  Dimensions for image: 200×52 pixels.
                </p>
                <OrganizationLogoPreview
                  src={ctx.logoPreviewSrc('org_logo')}
                  download={buildOrganizationLogoDownloadAction('org_logo', {
                    orgId: ctx.orgId,
                    isEdit: ctx.isEdit,
                    logoFiles: ctx.logoFiles,
                    logoBlobPreviewUrls: ctx.logoBlobPreviewUrls,
                    logoServerSrc: ctx.logoServerSrc,
                  })}
                />
              </div>

              <div className={companyFormStyles.formGroup}>
                <label className={companyFormStyles.formLabel}>
                  Organization collapsed logo
                </label>
                <IntegratedFileSelect
                  displayName={ctx.displayLogoLine('org_swoosh_logo')}
                  accept={ctx.logoImageAccept}
                  emptyLabel="No file selected"
                  browseLabel="Select"
                  disabled={ctx.submitting}
                  onChange={(e) => ctx.handleLogoFileChange('org_swoosh_logo', e)}
                  data-testid="org-swoosh-logo-upload"
                />
                <p className={companyFormStyles.infoText}>
                  Only JPG, JPEG &amp; PNG files are allowed. Preferred format is PNG for transparency.
                  Dimensions for image: 42×52 pixels.
                </p>
                <OrganizationLogoPreview
                  src={ctx.logoPreviewSrc('org_swoosh_logo')}
                  download={buildOrganizationLogoDownloadAction('org_swoosh_logo', {
                    orgId: ctx.orgId,
                    isEdit: ctx.isEdit,
                    logoFiles: ctx.logoFiles,
                    logoBlobPreviewUrls: ctx.logoBlobPreviewUrls,
                    logoServerSrc: ctx.logoServerSrc,
                  })}
                />
              </div>

              <div className={companyFormStyles.formGroup}>
                <label className={companyFormStyles.formLabel}>Login page logo</label>
                <IntegratedFileSelect
                  displayName={ctx.displayLogoLine('org_image')}
                  accept={ctx.logoImageAccept}
                  emptyLabel="No file selected"
                  browseLabel="Select"
                  disabled={ctx.submitting}
                  onChange={(e) => ctx.handleLogoFileChange('org_image', e)}
                  data-testid="org-login-logo-upload"
                />
                <p className={companyFormStyles.infoText}>
                  Only JPG, JPEG &amp; PNG files are allowed. Preferred format is PNG for transparency.
                  Dimensions for image: 42×52 pixels.
                </p>
                <OrganizationLogoPreview
                  src={ctx.logoPreviewSrc('org_image')}
                  download={buildOrganizationLogoDownloadAction('org_image', {
                    orgId: ctx.orgId,
                    isEdit: ctx.isEdit,
                    logoFiles: ctx.logoFiles,
                    logoBlobPreviewUrls: ctx.logoBlobPreviewUrls,
                    logoServerSrc: ctx.logoServerSrc,
                  })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <Palette className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Color scheme information
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className={companyFormStyles.formGroup}>
              <label className={companyFormStyles.formLabel} htmlFor="org-color">
                Organization primary color
              </label>
              <input
                id="org-color"
                type="color"
                className={cn(
                  'h-11 w-full max-w-[12rem] cursor-pointer rounded-lg border border-slate-300 bg-white p-1 shadow-sm',
                  ctx.errors.org_color && 'companyFormStyles.formControlError',
                )}
                {...ctx.register('org_color')}
              />
              {ctx.errors.org_color && (
                <span role="alert" className={companyFormStyles.errorText}>
                  {ctx.errors.org_color.message}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <Mail className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              Email signature information
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className={companyFormStyles.formGroup}>
              <div className="mb-4 flex w-full min-w-0 flex-wrap items-center gap-4">
                <span className={cn(companyFormStyles.formLabel, '!mb-0')}>
                  Email signature
                  <span className="text-red-500 ml-1">*</span>
                </span>
                <div
                  className="inline-flex min-w-0 flex-1 justify-start overflow-hidden rounded-md border-2 border-blue-500 sm:flex-initial"
                  role="tablist"
                  aria-label="Email signature body or preview"
                >
                  <button
                    type="button"
                    role="tab"
                    id="org-email-sig-body-tab"
                    aria-selected={ctx.emailSignatureView === 'body'}
                    aria-controls="org-email-sig-body-panel"
                    className={cn(
                      'min-w-0 flex-1 px-4 py-1.5 text-sm font-semibold transition-colors sm:min-w-[10rem] sm:flex-none',
                      ctx.emailSignatureView === 'body'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-blue-500 hover:bg-slate-50',
                    )}
                    onClick={() => ctx.setOrganizationEmailSignatureView('body')}
                  >
                    Email signature body
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="org-email-sig-preview-tab"
                    aria-selected={ctx.emailSignatureView === 'preview'}
                    aria-controls="org-email-sig-preview-panel"
                    className={cn(
                      'min-w-0 flex-1 border-l-2 border-blue-500 px-4 py-1.5 text-sm font-semibold transition-colors sm:min-w-[7rem] sm:flex-none',
                      ctx.emailSignatureView === 'preview'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-blue-500 hover:bg-slate-50',
                    )}
                    onClick={() => ctx.setOrganizationEmailSignatureView('preview')}
                  >
                    Preview
                  </button>
                </div>
              </div>
              <Controller
                name="email_footer"
                control={ctx.control}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <div className="w-full min-w-0">
                    <div
                      id="org-email-sig-body-panel"
                      role="tabpanel"
                      aria-labelledby="org-email-sig-body-tab"
                      className={ctx.emailSignatureView === 'body' ? 'block' : 'hidden'}
                      hidden={ctx.emailSignatureView !== 'body'}
                    >
                      <label htmlFor="org-email-footer" className="sr-only">
                        Email signature body (plain text; HTML is shown as text and can be edited)
                      </label>
                      <textarea
                        id="org-email-footer"
                        ref={ref}
                        rows={14}
                        spellCheck
                        className={cn(
                          companyFormStyles.customFormControl,
                          'min-h-[220px] w-full max-w-full resize-y font-sans leading-relaxed',
                          ctx.errors.email_footer && 'companyFormStyles.formControlError',
                        )}
                        value={value ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={onBlur}
                      />
                    </div>
                    <div
                      id="org-email-sig-preview-panel"
                      role="tabpanel"
                      aria-labelledby="org-email-sig-preview-tab"
                      className={ctx.emailSignatureView === 'preview' ? 'block' : 'hidden'}
                      hidden={ctx.emailSignatureView !== 'preview'}
                    >
                      <div className="w-full min-w-0 overflow-hidden rounded-md border border-slate-200 bg-white">
                        {ctx.emailSignatureView === 'preview' && (
                          <EmailFooterPreviewContent value={value ?? ''} />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              />
              {ctx.errors.email_footer && (
                <p className={companyFormStyles.errorText}>{ctx.errors.email_footer.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className={companyFormStyles.card}>
          <div className={companyFormStyles.cardHeader}>
            <Mail className={companyFormStyles.cardIcon} />
            <h2 className={companyFormStyles.cardTitle}>
              System announcement
            </h2>
          </div>
          <div className={companyFormStyles.cardBody}>
            <div className={companyFormStyles.formGroup}>
              <span className={companyFormStyles.formLabel}>Content</span>
              <Controller
                name="system_announcement"
                control={ctx.control}
                render={({ field: { value, onChange } }) => (
                  <CKEditor4 id="org-system-announcement" value={value || ''} onChange={onChange} />
                )}
              />
            </div>
          </div>
        </div>
        </div>
        </div>

  )
}
